import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { selectCustomer, setCustomer } from './customerSlice';
import { FormikErrors, useFormik } from 'formik';
import CustomerAPI from '../../API/customerAPI';
import { selectToken, selectUser } from '../user/userSlice';
import GooglePlace from '../../components/GooglePlace';
import { setCartZipcode, setOrderInProgress } from '../cart/cartSlice';
import OrdersAPI from '../../API/ordersAPI';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectGeneralOptions, selectIsMobileRoute } from '../mobile/mobileSlice';
import { resetOrderPlannerOrders, selectOrderPlannerOrders, updateOrderPlannerOrder } from '../../pages/AutoshipPages/core/orderPlannerOrdersSlice';
interface FormValues {
  first_name: string;
  last_name: string;
  address_1: string;
  address_2?: string;
  city: string;
  country: string;
  state: string;
  postcode: string;
}
interface Props {
  onClose: () => void;
}

export default function ShippingAddressForm({ onClose }: Props) {
  const navigate = useNavigate();
  const streetRef = useRef<any>()
  const townRef = useRef<any>()
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useSelector(selectToken);
  const [busy, setBusy] = useState(false);
  const [showAddressVerification, setShowAddressVerification] = useState(false);
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [addressResult, setAddressResult] = useState<any>(null);
  const [addressChange, setAddressChange] = useState('new-address');
  const [errorMsg, setErrorMsg] = useState('');
  const user = useSelector(selectUser);
  const customer = useSelector(selectCustomer);
  const AppURL = useSelector(selectIsMobileRoute);
  const getGeneralOptions = useSelector(selectGeneralOptions);
  const orderPlannerOrders = useSelector(selectOrderPlannerOrders);
  const plannedOrders = orderPlannerOrders.filter((order: any) => {
    return order.status === 'autoship' || order.status === 'autoship-onhold';
  });
  const urlParams = AppURL ? `/?${AppURL}` : '';
  const isEditableOrder = location?.state && location.state.isEditOrder === true;
  const updateOrderId = location?.state && location?.state?.orderId;
  const isAutoship = location?.state && location?.state?.autoShip;

  const paymentMode = getGeneralOptions?.payment_mode;
  const GoogleApiKey = (paymentMode === 'live' && process.env.REACT_APP_WEB_URL === "https://mightymeals.com/") ?
    String(process.env.REACT_APP_PROD_GOOGLE_API_KEY) :
    String(process.env.REACT_APP_STAGE_GOOGLE_API_KEY);

  const capitalizeWords = (str: string) => {
    return str
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const newResult = addressResult && addressResult.uspsData && addressResult.uspsData.standardizedAddress;
  const newStreet = newResult && newResult.firstAddressLine ? capitalizeWords(newResult.firstAddressLine) : "";
  const newCity = newResult && newResult.city ? capitalizeWords(newResult.city) : "";
  const newState = newResult && newResult.state;
  const newZipCode = newResult && newResult.zipCode;

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};
    if (!values.first_name) {
      errors.first_name = "Required";
    }
    if (!values.last_name) {
      errors.last_name = "Required";
    }
    if (!values.address_1) {
      errors.address_1 = "Required";
    }
    if (!values.city) {
      errors.city = "Required";
    }
    if (!values.state) {
      errors.state = "Required";
    }
    if (!values.postcode) {
      errors.postcode = "Required";
    } else if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.postcode)) {
      errors.postcode = "Invalid"
    }
    return errors;
  }

  const formik = useFormik({
    initialValues: {
      first_name: customer?.shipping?.first_name,
      last_name: customer?.shipping?.last_name,
      address_1: customer?.shipping?.address_1,
      address_2: customer?.shipping?.address_2,
      city: customer?.shipping?.city,
      country: "US",
      state: customer?.shipping?.state,
      postcode: customer?.shipping?.postcode
    },
    validate,
    onSubmit: async values => {
      setErrorMsg('');
      setBusy(true);
      if (!isAddressVerified) {
        const address1 = values.address_1;
        const city = values.city;
        const state = values.state;
        const postCode = values.postcode;

        const address = {
          regionCode: "US",
          postalCode: postCode,
          administrativeArea: state,
          locality: city,
          addressLines: [address1]
        };

        try {
          const response = await OrdersAPI.googleAddressverification(address, GoogleApiKey);

          const resultVerdict = response?.result?.verdict;
          const isPreMiseSubPremise = resultVerdict && (
            (["SUB_PREMISE", "PREMISE"].includes(resultVerdict.inputGranularity)) &&
            (["SUB_PREMISE", "PREMISE"].includes(resultVerdict.validationGranularity)) &&
            (["SUB_PREMISE", "PREMISE"].includes(resultVerdict.geocodeGranularity))
          );

          const isAddressNeedtoUpdate = resultVerdict &&
            (resultVerdict.hasReplacedComponents ||
              resultVerdict.hasUnconfirmedComponents ||
              !isPreMiseSubPremise);

          if (isAddressNeedtoUpdate) {
            setAddressResult(response.result);
            setShowAddressVerification(true);
            setBusy(false);
            return;
          }

        } catch (error) {
          console.warn("Address verification API failed. Proceeding anyway.", error);
        }
      }

      if (isEditableOrder) {
        UpdateEditOrder(values);
      } else {
        CustomerAPI.updateCustomerShipping({
          customer_id: customer?.id,
          token: user.token,
          shipping: { ...values }
        }).then((response) => {
          if ('code' in response) {
            console.error(response);
            setErrorMsg("An error has occured. Please try again later.");
          } else if ('id' in response) {
            dispatch(setCustomer(response));
            onClose();
          }
        }).catch((e) => {
          setErrorMsg(e.message);
        }).finally(() => {
          setBusy(false);
          if (plannedOrders.length > 0) {
            dispatch(resetOrderPlannerOrders());
          }
          if (isAutoship) {
            navigate(`/autoship${urlParams}`);
          }
        });
      }
    }
  });

  useEffect(() => {
    if (isAddressVerified) {
      setIsAddressVerified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.address_1, formik.values.city, formik.values.state, formik.values.postcode]);

  const UpdateEditOrder = async (values: any) => {
    try {
      dispatch(setOrderInProgress(true));

      const orderData: Record<string, any> = {
        shipping: { ...values, country: 'US' }
      };

      const response = await OrdersAPI.updateOrder(token, updateOrderId, orderData);
      OrdersAPI.createOrderNote(token, updateOrderId, "Customer changed shipping address.");

      if ('id' in response) {
        await dispatch(updateOrderPlannerOrder(response));
        navigate(`/autoship/orders/${updateOrderId}${urlParams}`);
      }

    } catch (e) {
      console.error("Error While Updating Order: ", e);
    } finally {
      dispatch(setOrderInProgress(false));
      setBusy(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAddressChange(e.target.id);
  }

  const handleAddressVerification = async () => {
    if (addressChange === 'new-address') {
      const address1 = 'address_1';
      const city = 'city';
      const state = 'state';
      const postCode = 'postcode';

      formik.setFieldValue(address1, newStreet);
      formik.setFieldValue(city, newCity);
      formik.setFieldValue(state, newState);
      formik.setFieldValue(postCode, newZipCode);
      await dispatch(setCartZipcode(newZipCode));
    }
    setIsAddressVerified(true);
    setShowAddressVerification(false);
  }

  return (
    <Card className="shipping-address-form">
      <Modal style={{ top: '20%' }} show={showAddressVerification} backdrop="static" onHide={() => setShowAddressVerification(false)} keyboard={false}>
        <Modal.Header className='border-bottom-0' closeButton>
          <div className='text-center d-flex justify-content-center w-100 fs-4'>
            <p className='mt-2'>Address verification</p>
          </div>
        </Modal.Header>
        <Modal.Body className='font-barlow'>
          <div className='text-center'>
            <p className='mb-0'>The address you entered appears to be incorrect or incomplete.</p>
            <p>Please verify your address to ensure a timely delivery.</p>
          </div>
          <Form className='fs-6 mx-2 text-center d-flex flex-row justify-content-between'>
            <div className="border p-3 w-50 text-start">
              <Form.Check
                type="radio"
                name="address-field"
                label="Use As Entered"
                id="old-address"
                checked={addressChange === "old-address"}
                onChange={handleChange}
              />
              <p className='mt-2'>{formik.values.address_1}, {formik.values.city}, {formik.values.state}, {formik.values.postcode}</p>
            </div>
            <div className="border p-3 w-50 ms-3 text-start">
              <Form.Check
                type="radio"
                name="address-field"
                label="Verified Address"
                id="new-address"
                checked={addressChange === "new-address"}
                onChange={handleChange}
              />
              <p className='mt-2'>{newStreet}, {newCity}, {newState}, {newZipCode}</p>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className='border-top-0'>
          <div className='d-flex justify-content-end'>
            <Button variant="dark" className='mx-2' onClick={() => setShowAddressVerification(false)}>
              Cancel
            </Button>
            <Button variant="success" className='text-white' onClick={handleAddressVerification}>
              Submit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <Card.Header><span className="fs-4">Shipping Address</span></Card.Header>
      <Card.Body>
        <Form onSubmit={formik.handleSubmit}>
          {errorMsg && (
            <p className='text-danger'>{errorMsg}</p>)}
          <div className="row">
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  id="first_name"
                  type="text"
                  isValid={Boolean(formik.values.first_name) &&
                    !Boolean(formik.errors.first_name)}
                  isInvalid={Boolean(formik.errors.first_name)}
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                />
                {formik.errors.first_name &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.first_name}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  id="last_name"
                  type="text"
                  isValid={Boolean(formik.values.last_name) &&
                    !Boolean(formik.errors.last_name)}
                  isInvalid={Boolean(formik.errors.last_name)}
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                />
                {formik.errors.last_name &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.last_name}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3 form-group required">
            <Form.Label>Street address</Form.Label>
            <GooglePlace formik={formik} streetRef={streetRef} townRef={townRef} name={'address_1'}
              city={'city'} state={'state'} postcode={'postcode'}
            />
            {formik.errors.address_1 &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.address_1}
              </Form.Control.Feedback>}
          </Form.Group>
          <Form.Group className="mb-3 form-group">
            <Form.Control
              id="address_2"
              type="text"
              placeholder="Apartment, suite, unit etc. (optional)"
              isInvalid={Boolean(formik.errors.address_2)}
              value={formik.values.address_2}
              onChange={formik.handleChange}
            />
            {formik.errors.address_2 &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.address_2}
              </Form.Control.Feedback>}
          </Form.Group>
          <Form.Group className="mb-3 form-group required">
            <Form.Label>Town / City</Form.Label>
            <Form.Control
              id="city"
              ref={townRef}
              type="text"
              name="city"
              placeholder="Enter Town/Area"
              value={formik.values.city}
              onChange={formik.handleChange}

            />
            {formik.errors.city &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.city}
              </Form.Control.Feedback>}
          </Form.Group>
          <p>Delivery is to Residential addresses only. Non-residential deliveries
            will be canceled and are not refundable.
          </p>
          <div className="row">
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>State</Form.Label>
                <Form.Select
                  id="state"
                  isInvalid={Boolean(formik.errors.state)}
                  value={formik.values.state}
                  onChange={formik.handleChange}
                >
                  <option value="">-- select a state --</option>
                  <option value="AL">Alabama</option>
                  <option value="CT">Connecticut</option>
                  <option value="DC">District of Columbia</option>
                  <option value="DE">Delaware</option>
                  <option value="GA">Georgia</option>
                  <option value="IL">Illinois</option>
                  <option value="IA">Iowa</option>
                  <option value="IN">Indiana</option>
                  <option value="KY">Kentucky</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MO">Missouri</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="OH">Ohio</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="TN">Tennessee</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                </Form.Select>
                {formik.errors.state &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.state}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>Postcode / ZIP</Form.Label>
                <Form.Control
                  id="postcode"
                  type="text"
                  maxLength={10}
                  placeholder="Enter Postcode / ZIP"
                  value={formik.values.postcode}
                  onChange={formik.handleChange}
                />
                {formik.errors.postcode &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.postcode}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
          </div>
          <Button className="me-2" variant="outline-dark" onClick={onClose}>Cancel</Button>
          {busy ?
            <Button className='bg-black' size="lg" disabled>
              <Spinner animation="border" as="span" size="sm" />
              &nbsp;&nbsp;Saving ...
            </Button>
            :
            <Button className='bg-black' type="submit">Save address</Button>
          }
        </Form>
      </Card.Body>
    </Card>
  );
}